import styled from 'styled-components';

// Defined once in _constants (shared with ui-kit's sticky tables); re-exported for existing imports.
import { TOP_BAR_HEIGHT } from '_constants/LAYOUT';

export { TOP_BAR_HEIGHT };

const TEAL = '#2B4E56';
const TEXT = 'rgb(255, 237, 220)';
const CORAL = '#F5A687';
const PANEL_BG = '#FFFFFF';
const PANEL_BORDER = 'rgba(43, 78, 86, 0.12)';
const PANEL_TEXT = '#2B4E56';
const PANEL_MUTED = 'rgba(43, 78, 86, 0.65)';
const DANGER = '#D9534F';

export const TopBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above the rail (1000): this element is the stacking context for the notifications and account
     panels rendered inside it, so whatever it resolves to at root level is what those panels get.
     See the note in Rail/styles.js. */
  z-index: 1010;
  display: flex;
  align-items: center;
  gap: 16px;
  height: ${TOP_BAR_HEIGHT}px;
  padding: 0 16px 0 0;
  background: ${TEAL};
  color: ${TEXT};
`;

export const LogoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-left: 14px;
`;

export const BrandName = styled.span`
  color: ${TEXT};
  font-weight: 700;
  letter-spacing: 0.02em;
  font-size: 16px;
  line-height: 1;
`;

export const PostWordmark = styled.span`
  color: ${CORAL};
  font-weight: 700;
  letter-spacing: 0.04em;
  font-size: 16px;
  line-height: 1;
`;

export const AccountProduct = styled.span`
  color: ${TEXT};
  opacity: 0.8;
  font-size: 12px;
  padding-left: 8px;
  border-left: 1px solid rgba(255, 237, 220, 0.25);
  margin-left: 4px;
`;

export const SearchTrigger = styled.button`
  flex: 1;
  max-width: 560px;
  height: 28px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 237, 220, 0.18);
  border-radius: 4px;
  color: rgba(255, 237, 220, 0.7);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 237, 220, 0.5);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.18);
    border-color: ${CORAL};
    box-shadow: 0 0 0 2px rgba(245, 166, 135, 0.5);
  }
`;

export const SearchPlaceholder = styled.span`
  flex: 1;
`;

export const KbdChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 3px;
  background: rgba(255, 237, 220, 0.12);
  color: ${TEXT};
  font-size: 11px;
  font-weight: 500;
`;

export const RightCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
  flex-shrink: 0;
`;

export const IconButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  color: ${TEXT};
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.16);
    box-shadow: 0 0 0 2px rgba(245, 166, 135, 0.6);
  }
`;

export const BellBadge = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: ${CORAL};
  color: ${TEAL};
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const AccountWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const PublicTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  background: transparent;
  border: 1px solid rgba(255, 237, 220, 0.32);
  border-radius: 4px;
  color: ${TEXT};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 237, 220, 0.5);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.16);
    border-color: ${CORAL};
    box-shadow: 0 0 0 2px rgba(245, 166, 135, 0.5);
  }
`;

export const LoginButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 14px;
  background: ${CORAL};
  border: 0;
  border-radius: 4px;
  color: ${TEAL};
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: #ffba9c;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 237, 220, 0.7);
  }
`;

export const AvatarTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px 2px 2px;
  background: transparent;
  border: 0;
  border-radius: 16px;
  cursor: pointer;
  color: ${TEXT};
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  &:focus-visible {
    outline: none;
    background: rgba(255, 255, 255, 0.16);
    box-shadow: 0 0 0 2px rgba(245, 166, 135, 0.6);
  }
`;

export const AvatarCircle = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${CORAL};
  color: ${TEAL};
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const AvatarChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${TEXT};
  opacity: 0.85;
  transition: transform 120ms ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

export const AccountPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  min-width: 260px;
  padding: 6px;
  background: ${PANEL_BG};
  color: ${PANEL_TEXT};
  border: 1px solid ${PANEL_BORDER};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

export const AccountHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 8px;
`;

export const AccountHeaderAvatar = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${CORAL};
  color: ${TEAL};
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const AccountHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const AccountName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${PANEL_TEXT};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AccountEmail = styled.span`
  font-size: 12px;
  color: ${PANEL_MUTED};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AccountDivider = styled.div`
  height: 1px;
  margin: 4px 6px;
  background: ${PANEL_BORDER};
`;

export const AccountItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  font-size: 13px;
  color: ${({ $danger }) => ($danger ? DANGER : PANEL_TEXT)};
  transition: background 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'rgba(217, 83, 79, 0.12)' : 'rgba(43, 78, 86, 0.10)')};
  }

  &:focus-visible {
    outline: none;
    background: ${({ $danger }) => ($danger ? 'rgba(217, 83, 79, 0.12)' : 'rgba(43, 78, 86, 0.10)')};
    box-shadow: 0 0 0 2px ${({ $danger }) => ($danger ? 'rgba(217, 83, 79, 0.45)' : 'rgba(43, 78, 86, 0.35)')};
  }
`;

export const AccountItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: currentColor;
  flex-shrink: 0;
`;

export const AccountItemLabel = styled.span`
  font-weight: 500;
`;
